import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { PasswordToggleDirective } from '@common/directives';
import { RoutePath } from '@common/enums';
import { ArchivePasswordService } from '@common/services';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { confirmPasswordValidator, getErrorText, trackModalRequest } from '@common/utils';
import { I18NextPipe } from 'angular-i18next';
import { I18_NAMESPACE_APPS_UI } from 'i18n';
import {
  AutoFocusDirective,
  ButtonComponent,
  FormGroupComponent,
  InputComponent,
  MbsValidators,
  ModalComponent,
  ModalFooterDirective
} from 'mbs-ui-kit';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { EMPTY, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { FormKey, FormStructure } from './download-to-pst-file.model';

const components = [ButtonComponent, FormGroupComponent, InputComponent, ModalComponent];
const directives = [AutoFocusDirective, ModalFooterDirective, PasswordToggleDirective, RouterLink];
const modules = [IconSpriteModule, ReactiveFormsModule];
const pipes = [I18NextPipe];

export type DownloadToPstFileModalData = {
  id: string;
  taskHasStarted: boolean;
  func?: () => Observable<unknown>;
  countRequired: string;
};

@Component({
  imports: [...components, ...directives, ...modules, ...pipes],
  templateUrl: './download-to-pst-file.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DownloadToPstFileComponent implements OnInit {
  private ability = inject(AbilityService);
  private archivePasswordService = inject(ArchivePasswordService);
  private authContext = inject(AuthContextService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private i18nPipe = inject(I18NextPipe);

  private readonly trackRequestFn = trackModalRequest();

  private readonly baseModal = viewChild.required<ModalComponent<DownloadToPstFileModalData>>(ModalComponent);

  protected readonly RoutePath = RoutePath;
  protected readonly moduleButton = I18_NAMESPACE_APPS_UI.button;
  protected readonly FormKey = FormKey;

  protected formGroup!: FormGroup<FormStructure>;

  protected readonly prefix = this.authContext.prefix;
  protected readonly domainName = this.authContext.domainName;

  protected readonly isSingleUser = this.ability.isSingleUser;
  protected readonly isLimitedAdmin = this.ability.isLimitedAdmin;
  protected readonly isAdmin = this.ability.isAdmin;

  protected readonly loading = signal<boolean>(false);
  protected readonly buttonLoading = signal<boolean>(false);

  protected readonly isAnyAdmin = computed<boolean>(() => this.isAdmin() || this.isSingleUser() || this.isLimitedAdmin());

  protected readonly baseModalData = computed(() => this.baseModal().data);
  protected readonly countRequired = computed<string>(() => this.baseModalData().countRequired);
  protected readonly id = computed<string>(() => this.baseModalData().id);
  protected readonly taskHasStarted = computed<boolean>(() => this.baseModalData().taskHasStarted);

  ngOnInit(): void {
    this.initForm();
    this.initStreams();
  }

  private initForm(): void {
    this.formGroup = this.fb.group(
      {
        [FormKey.Password]: ['', [Validators.required, MbsValidators.passwordValidator, Validators.maxLength(20)]],
        [FormKey.ConfirmPassword]: ['', [Validators.required]]
      },
      {
        validators: [confirmPasswordValidator(FormKey.Password, FormKey.ConfirmPassword)]
      }
    );
  }

  private initStreams(): void {
    const func = this.baseModalData().func;

    if (!func) return;

    this.loading.set(true);
    func()
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  handleSave(): void {
    const { password } = this.formGroup.value;
    const request$ = this.isAnyAdmin() ? this.archivePasswordService.exportSettings(this.id(), password) : EMPTY;

    this.buttonLoading.set(true);
    request$
      .pipe(
        finalize(() => this.buttonLoading.set(false)),
        this.trackRequestFn
      )
      .subscribe({
        next: () => this.baseModal().save(true),
        error: (res: HttpErrorResponse) => this.handleFormError(res, this.formGroup) // it was added for a case if API return error after saving password, but that's unlikely
      });
  }

  private handleFormError(res: HttpErrorResponse, form: FormGroup<FormStructure>): void {
    const somethingWrongText = this.i18nPipe.transform('common.validation.somethingWrong', { format: 'capitalize' });

    if (res.status === 400) {
      form.get(FormKey.Password).setValue('');
      form.get(FormKey.Password).setErrors({
        [`Password Incorrect`]: { message: getErrorText(res, somethingWrongText) }
      });
    } else {
      form.reset();
      form.setErrors({
        password: { message: somethingWrongText }
      });
    }
  }

  handleClose(): void {
    this.baseModal().close();
  }
}
